import { useEffect } from "react";
import { useMount } from "constants/constants";
import {
  clearAllSegments,
  hidePnlChartCategories,
  setFocusRange,
} from "providers/GlobalStateProvider";
import { useDispatch } from "react-redux";
import {
  useDetectSerialProcess,
  useDetectDoesCustomerHaveCounts,
  useShifts,
  useGetPageFromPath,
  useGoToNow,
} from "utils/customHooks/genericHooks";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { TrendChartBinSizesEnum } from "types/enums";
import { useResetTrendChartBinSize } from "../dashboardPagesUtils";
import { getShiftIndex } from "components/Dashboard/range-selectors/steppable-range-selectors/ShiftSelector/shiftUtils";
import { CATEGORIES_TO_HIDE_PER_CUSTOMER } from "constants/customers";
import useQueryString from "utils/customHooks/useQueryString";
import { useIsTimelineExpanded } from "components/Dashboard/HubletView/SingleTimeline/singleTimelineStore";
import { usePrefetchHubletTags } from "components/Dashboard/Timeline/RetrotagPopper/retrotagQueryHooks";

export default function InitializeDashboardPagesHooks() {
  const dispatch = useDispatch();
  const { isGroupTilesPage, isProcessTilesPage } = useGetPageFromPath();

  // for customers that operate with a single machine
  // as constraint / bottleneck set it here
  // (detect if we're looking at a serial process)
  useDetectSerialProcess();

  // detect if the customer uses counts
  useDetectDoesCustomerHaveCounts();

  // hide certain PnL categories for certain customers
  useHidePnlCategoriesPerCustomer();

  // set the focus range (if we're coming from trends page, old focus range remains)
  useSetFocusRangeOnMount();

  // prefetch hublet tags data for the RetrotagPopper "open tag sheet" button
  usePrefetchHubletTags();

  // clear all segments when we visit the group tiles page
  useEffect(() => {
    if (isGroupTilesPage) {
      dispatch(clearAllSegments());
    }
  }, [isGroupTilesPage, dispatch]);

  // return to the current time on group tiles / process tiles pages
  const goToNow = useGoToNow();
  useEffect(() => {
    if (isGroupTilesPage || isProcessTilesPage) {
      goToNow();
    }
  }, [isGroupTilesPage, isProcessTilesPage, goToNow]);

  // set trend chart to hourly bin size (it's the only size allowed on Dashboard Pages)
  useResetTrendChartBinSize({
    newTrendChartBinSize: TrendChartBinSizesEnum.hours,
  });

  // if we see "&sku=..." in the query string, set isTimelineExpanded to true
  const [, setIsTimelineExpanded] = useIsTimelineExpanded();
  useMount(() => {
    if (window.location.search.includes("sku=")) {
      setIsTimelineExpanded(true);
    }
  });

  return null;
}

/** set focus range to current shift, or from ge, lt in query string */
export function useSetFocusRangeOnMount() {
  const dispatch = useDispatch();
  const shifts = useShifts();
  const parsedQuery = useQueryString();
  useMount(() => {
    if (shifts) {
      const isCustomRangeSelector = parsedQuery.ge && parsedQuery.lt;
      const currentShift = shifts[getShiftIndex(shifts, Date.now())];

      if (isCustomRangeSelector) {
        dispatch(
          setFocusRange({
            ge: Number(parsedQuery.ge),
            lt: Number(parsedQuery.lt),
          })
        );
      } else if (!isCustomRangeSelector && currentShift?.range) {
        dispatch(setFocusRange(currentShift.range));
      }
    }
  });
}

export function useHidePnlCategoriesPerCustomer() {
  const dispatch = useDispatch();
  const ckey = useCustomerKey();
  const categoriesToHide = CATEGORIES_TO_HIDE_PER_CUSTOMER[ckey];

  useEffect(() => {
    if (categoriesToHide) {
      // e.g. hide categories "notScheduled", "producing", "waiting",
      // on the Pnl chart
      dispatch(hidePnlChartCategories(categoriesToHide));
    }
  }, [categoriesToHide, dispatch]);
}
